import { normalizeAccountStatus } from "@/lib/accountDeletion/status";
import { resolveImageSrc } from "@/lib/safeImage";
import { getSupabaseBrowserClient } from "@/lib/supabase/browser";
import { logMutation, requireSession } from "@/lib/auth/requireSession";
import { getOrderPickupFulfillmentLine } from "@/lib/orders/fulfillmentDates";
import { getOrderStatusTimestamp } from "@/lib/orders";
import { fetchPublicProfilesByIds } from "@/lib/users/publicProfiles";

export const MESSAGE_PAGE_SIZE = 50;
export const CONVERSATION_PAGE_SIZE = 40;
export const MESSAGE_SELECT_COLUMNS =
  "id, conversation_id, sender_id, recipient_id, actor_user_id, body, created_at, read_at, type, order_id, order_number, status, timestamp, fulfillment_type, business_name, order_items";
const REAL_MESSAGE_SELECT_COLUMNS =
  "id, conversation_id, sender_id, recipient_id, actor_user_id, body, created_at, read_at, type";
const ORDER_CONTEXT_MESSAGE_SELECT_COLUMNS =
  "id, conversation_id, sender_id, recipient_id, body, created_at, type, order_id, order_number, status, timestamp, fulfillment_type, business_name, order_items";
const REAL_MESSAGE_TYPE_FILTER =
  "type.is.null,and(type.neq.order_status_update,type.neq.system_order_update)";
const SYSTEM_ORDER_MESSAGE_TYPES = new Set([
  "order_status_update",
  "system_order_update",
]);
const ORDER_CONVERSATION_PREVIEW = "Order conversation";

export function getDisplayName(profile: {
  business_name?: string | null;
  full_name?: string | null;
  account_status?: string | null;
} | null) {
  if (normalizeAccountStatus(profile?.account_status) === "deleted") {
    return "Deleted user";
  }
  return profile?.business_name || profile?.full_name || "Unknown";
}

export function getAvatarUrl(profile: {
  profile_photo_url?: string | null;
  account_status?: string | null;
} | null) {
  if (normalizeAccountStatus(profile?.account_status) === "deleted") {
    return resolveImageSrc(null, "/business-placeholder.png");
  }
  return resolveImageSrc(
    profile?.profile_photo_url,
    "/business-placeholder.png"
  );
}

export function getUnreadCount(
  conversation: {
    customer_unread_count?: number | null;
    business_unread_count?: number | null;
  },
  role: "customer" | "business"
) {
  return role === "business"
    ? Number(conversation?.business_unread_count || 0)
    : Number(conversation?.customer_unread_count || 0);
}

function normalizeMessageType(value: unknown) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

export function isSystemOrderMessage(message: { type?: unknown } | null | undefined) {
  return SYSTEM_ORDER_MESSAGE_TYPES.has(normalizeMessageType(message?.type));
}

export function isRealConversationMessage(message: { type?: unknown } | null | undefined) {
  return !isSystemOrderMessage(message);
}

export function filterRealConversationMessages(messages: any[] = []) {
  return (Array.isArray(messages) ? messages : []).filter(isRealConversationMessage);
}

export async function fetchConversations({
  supabase,
  userId,
  role,
  onTiming = null,
}: {
  supabase?: any;
  userId: string;
  role: "customer" | "business";
  onTiming?: ((payload: Record<string, unknown>) => void | Promise<void>) | null;
}) {
  const client = supabase ?? getSupabaseBrowserClient();
  if (!client) return [];

  const diagEnabled =
    process.env.NODE_ENV === "development" &&
    process.env.NEXT_PUBLIC_MSG_DIAG === "1";
  const startTime = diagEnabled ? Date.now() : 0;

  const idField = role === "business" ? "business_id" : "customer_id";
  const queryStart = typeof performance !== "undefined" ? performance.now() : Date.now();
  const { data, error } = await client
    .from("conversations")
    .select(
      [
        "id",
        "customer_id",
        "business_id",
        "last_message_at",
        "last_message_preview",
        "customer_unread_count",
        "business_unread_count",
      ].join(",")
    )
    .eq(idField, userId)
    .order("last_message_at", { ascending: false })
    .limit(CONVERSATION_PAGE_SIZE);
  const queryMs =
    (typeof performance !== "undefined" ? performance.now() : Date.now()) -
    queryStart;

  if (error) throw error;
  const rows = Array.isArray(data) ? data : [];
  const profileIds =
    role === "business"
      ? rows.map((row) => row.customer_id)
      : rows.map((row) => row.business_id);
  const profileStart = typeof performance !== "undefined" ? performance.now() : Date.now();
  const profiles = await fetchProfilesByIds({
    supabase: client,
    ids: profileIds,
  });
  const profileMs =
    (typeof performance !== "undefined" ? performance.now() : Date.now()) -
    profileStart;

  const realMessageByConversation = await fetchLatestRealMessageByConversation({
    supabase: client,
    conversationIds: rows.map((row) => row.id),
  });

  const conversations = rows.map((row) => {
    const latestRealMessage = realMessageByConversation[row.id] ?? null;
    return {
      ...row,
      last_message_at:
        latestRealMessage?.created_at
          ? latestRealMessage.created_at
          : row.last_message_at,
      last_message_preview:
        latestRealMessage
          ? latestRealMessage.body || ""
          : ORDER_CONVERSATION_PREVIEW,
      customer:
        role === "business" ? profiles[row.customer_id] ?? null : null,
      business:
        role === "customer" ? profiles[row.business_id] ?? null : null,
    };
  });

  if (diagEnabled && typeof window !== "undefined") {
    const durationMs = Date.now() - startTime;
    console.log("[MSG_DIAG]", {
      phase: "fetchConversations",
      role,
      userId,
      durationMs,
      count: conversations.length,
    });
  }

  if (typeof onTiming === "function") {
    await onTiming({
      role,
      conversationCount: conversations.length,
      queryMs: Math.round(queryMs),
      profileMs: Math.round(profileMs),
      totalMs: Math.round(queryMs + profileMs),
    });
  }

  return conversations;
}

export async function fetchConversationById({
  supabase,
  conversationId,
}: {
  supabase?: any;
  conversationId: string;
}) {
  const data = await fetchConversationBaseById({
    supabase,
    conversationId,
  });
  if (!data) return null;

  return hydrateConversationProfiles({
    supabase,
    conversation: data,
  });
}

export async function fetchConversationWithMessages({
  supabase,
  conversationId,
  limit = MESSAGE_PAGE_SIZE,
  profileRole = null,
  onTiming = null,
}: {
  supabase?: any;
  conversationId: string;
  limit?: number;
  profileRole?: "customer" | "business" | null;
  onTiming?: ((payload: Record<string, unknown>) => void | Promise<void>) | null;
}) {
  const client = supabase ?? getSupabaseBrowserClient();
  if (!client) return { conversation: null, messages: [] };

  const conversationStart = typeof performance !== "undefined" ? performance.now() : Date.now();
  const conversation = await fetchConversationBaseById({
    supabase: client,
    conversationId,
  });
  const conversationLookupMs =
    (typeof performance !== "undefined" ? performance.now() : Date.now()) -
    conversationStart;

  if (!conversation) {
    if (typeof onTiming === "function") {
      await onTiming({
        outcome: "not_found",
        conversationLookupMs: Math.round(conversationLookupMs),
        totalMs: Math.round(conversationLookupMs),
      });
    }
    return { conversation: null, messages: [] };
  }

  const hydrateStart = typeof performance !== "undefined" ? performance.now() : Date.now();
  const messageStart = typeof performance !== "undefined" ? performance.now() : Date.now();
  const [hydratedConversation, messagePage] = await Promise.all([
    hydrateConversationProfiles({
      supabase: client,
      conversation,
      profileRole,
    }),
    fetchMessagePage({
      supabase: client,
      conversationId,
      limit,
      includeSystemOrderUpdates: false,
    }),
  ]);
  const profileHydrationMs =
    (typeof performance !== "undefined" ? performance.now() : Date.now()) -
    hydrateStart;
  const messagesQueryMs =
    (typeof performance !== "undefined" ? performance.now() : Date.now()) -
    messageStart;

  if (typeof onTiming === "function") {
    await onTiming({
      profileRole,
      conversationLookupMs: Math.round(conversationLookupMs),
      profileHydrationMs: Math.round(profileHydrationMs),
      messagesQueryMs: Math.round(messagesQueryMs),
      messageCount: messagePage.messages.length,
      totalMs: Math.round(
        conversationLookupMs + Math.max(profileHydrationMs, messagesQueryMs)
      ),
    });
  }

  return {
    conversation: hydratedConversation,
    messages: messagePage.messages,
    hasMore: messagePage.hasMore,
  };
}

async function fetchConversationBaseById({
  supabase,
  conversationId,
}: {
  supabase?: any;
  conversationId: string;
}) {
  const client = supabase ?? getSupabaseBrowserClient();
  if (!client) return null;

  const { data, error } = await client
    .from("conversations")
    .select(
      [
        "id",
        "customer_id",
        "business_id",
        "last_message_at",
        "last_message_preview",
        "customer_unread_count",
        "business_unread_count",
      ].join(",")
    )
    .eq("id", conversationId)
    .maybeSingle();

  if (error) throw error;
  return data ?? null;
}

async function hydrateConversationProfiles({
  supabase,
  conversation,
  profileRole = null,
}: {
  supabase?: any;
  conversation: any;
  profileRole?: "customer" | "business" | null;
}) {
  const client = supabase ?? getSupabaseBrowserClient();
  if (!client || !conversation) return conversation ?? null;
  const ids =
    profileRole === "business"
      ? [conversation.business_id]
      : profileRole === "customer"
        ? [conversation.customer_id]
        : [conversation.customer_id, conversation.business_id];

  const profiles = await fetchProfilesByIds({
    supabase: client,
    ids,
  });

  return {
    ...conversation,
    customer:
      profileRole && profileRole !== "customer"
        ? null
        : profiles[conversation.customer_id] ?? null,
    business:
      profileRole && profileRole !== "business"
        ? null
        : profiles[conversation.business_id] ?? null,
  };
}

export function getOtherConversationProfile({
  conversation,
  currentUserId,
}: {
  conversation?: any;
  currentUserId?: string | null;
}) {
  if (!conversation) return null;
  return conversation.customer_id === currentUserId
    ? conversation.business ?? null
    : conversation.customer ?? null;
}

export async function fetchMessages({
  supabase,
  conversationId,
  limit = MESSAGE_PAGE_SIZE,
  before,
  beforeId,
  includeSystemOrderUpdates = true,
}: {
  supabase?: any;
  conversationId: string;
  limit?: number;
  before?: string | null;
  beforeId?: string | null;
  includeSystemOrderUpdates?: boolean;
}) {
  const page = await fetchMessagePage({
    supabase,
    conversationId,
    limit,
    before,
    beforeId,
    includeSystemOrderUpdates,
  });
  return page.messages;
}

export async function fetchMessagePage({
  supabase,
  conversationId,
  limit = MESSAGE_PAGE_SIZE,
  before,
  beforeId,
  includeSystemOrderUpdates = true,
}: {
  supabase?: any;
  conversationId: string;
  limit?: number;
  before?: string | null;
  beforeId?: string | null;
  includeSystemOrderUpdates?: boolean;
}) {
  const client = supabase ?? getSupabaseBrowserClient();
  if (!client) return { messages: [], hasMore: false };
  const pageSize = Math.min(Math.max(Number(limit) || MESSAGE_PAGE_SIZE, 1), 100);

  let query = client
    .from("messages")
    .select(MESSAGE_SELECT_COLUMNS)
    .eq("conversation_id", conversationId)
    .order("created_at", { ascending: false })
    .order("id", { ascending: false })
    .limit(pageSize + 1);

  if (before) {
    query = beforeId
      ? query.or(
          `created_at.lt.${before},and(created_at.eq.${before},id.lt.${beforeId})`
        )
      : query.lt("created_at", before);
  }
  if (!includeSystemOrderUpdates) {
    query = query.or(REAL_MESSAGE_TYPE_FILTER);
  }

  const { data, error } = await query;
  if (error) throw error;
  const rows = Array.isArray(data) ? data : [];
  const filteredRows = includeSystemOrderUpdates
    ? rows
    : filterRealConversationMessages(rows);
  const visibleRows = filteredRows.slice(0, pageSize);
  const messages = await hydrateMessageActorProfiles({
    supabase: client,
    messages: visibleRows.reverse(),
  });
  return {
    messages,
    hasMore: filteredRows.length > pageSize,
  };
}

async function hydrateMessageActorProfiles({
  supabase,
  messages,
}: {
  supabase?: any;
  messages: any[];
}) {
  const client = supabase ?? getSupabaseBrowserClient();
  const actorIds = Array.from(
    new Set(
      (Array.isArray(messages) ? messages : [])
        .map((message) => message?.actor_user_id)
        .filter(Boolean)
    )
  );
  if (!client || actorIds.length === 0) return messages;

  try {
    const profiles = await fetchProfilesByIds({
      supabase: client,
      ids: actorIds,
    });
    return messages.map((message) => {
      const actor = message?.actor_user_id ? profiles[message.actor_user_id] : null;
      if (!actor) return message;
      return {
        ...message,
        actor_display_name: getDisplayName(actor),
      };
    });
  } catch {
    return messages;
  }
}

async function fetchLatestRealMessageByConversation({
  supabase,
  conversationIds,
}: {
  supabase?: any;
  conversationIds: string[];
}) {
  const client = supabase ?? getSupabaseBrowserClient();
  const ids = Array.from(new Set((conversationIds || []).filter(Boolean)));
  if (!client || ids.length === 0) return {};

  const { data, error } = await client
    .from("messages")
    .select(REAL_MESSAGE_SELECT_COLUMNS)
    .in("conversation_id", ids)
    .or(REAL_MESSAGE_TYPE_FILTER)
    .order("created_at", { ascending: false })
    .limit(Math.max(ids.length * 25, 100));

  if (error) throw error;

  const latestByConversation: Record<string, any> = {};
  (Array.isArray(data) ? data : []).forEach((message) => {
    if (!message?.conversation_id) return;
    if (!isRealConversationMessage(message)) return;
    if (!latestByConversation[message.conversation_id]) {
      latestByConversation[message.conversation_id] = message;
    }
  });
  return latestByConversation;
}

export async function fetchConversationOrderContext({
  supabase,
  conversationId,
}: {
  supabase?: any;
  conversationId: string;
}) {
  const client = supabase ?? getSupabaseBrowserClient();
  if (!client || !conversationId) return null;

  const { data: message, error: messageError } = await client
    .from("messages")
    .select(ORDER_CONTEXT_MESSAGE_SELECT_COLUMNS)
    .eq("conversation_id", conversationId)
    .eq("type", "order_status_update")
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (messageError) throw messageError;
  if (!message) return null;

  let order = null;
  if (message.order_id) {
    const { data: orderRow, error: orderError } = await client
      .from("orders")
      .select(
        "id,order_number,status,fulfillment_type,pickup_time,delivery_time,updated_at,created_at,paid_at,payment_authorized_at,confirmed_at,ready_at,out_for_delivery_at,fulfilled_at,cancelled_at,payment_cancelled_at,order_items(id,fulfillment_date,fulfillment_time_start,fulfillment_time_end)"
      )
      .eq("id", message.order_id)
      .maybeSingle();
    if (orderError) throw orderError;
    order = orderRow ?? null;
  }

  const orderNumber = order?.order_number || message.order_number || "";
  const fulfillmentType =
    order?.fulfillment_type || message.fulfillment_type || null;
  const pickupLine = order ? getOrderPickupFulfillmentLine(order) : null;
  const schedule =
    fulfillmentType === "delivery"
      ? order?.delivery_time
      : pickupLine || order?.pickup_time;
  const fulfillmentLabel = fulfillmentType
    ? pickupLine ||
      `${fulfillmentType === "delivery" ? "Delivery" : "Pickup"}${
        schedule ? ` · ${schedule}` : ""
      }`
    : "";

  return {
    orderId: order?.id || message.order_id || null,
    orderNumber,
    status: order?.status || message.status || "",
    fulfillmentType,
    fulfillmentLabel,
    updatedAt:
      getOrderStatusTimestamp(order) ||
      message.timestamp ||
      message.created_at ||
      null,
    businessName: message.business_name || "",
    viewHref: orderNumber
      ? `/orders/${encodeURIComponent(orderNumber)}?from=messages`
      : "/account/orders",
  };
}

export async function sendMessage({
  supabase,
  conversationId,
  recipientId,
  body,
  session,
}: {
  supabase?: any;
  conversationId: string;
  recipientId: string;
  body: string;
  session?: any;
}) {
  const client = supabase ?? getSupabaseBrowserClient();
  if (!client) return null;

  const activeSession =
    session ?? (await requireSession(client, { label: "sendMessage" }));
  const sessionUserId = activeSession?.user?.id ?? null;

  logMutation("sendMessage", {
    stage: "start",
    conversationId,
    senderId: sessionUserId,
    recipientId,
    hasAccessToken: Boolean(activeSession?.access_token),
  });

  try {
    const { data, error } = await client
      .from("messages")
      .insert({
        conversation_id: conversationId,
        sender_id: sessionUserId,
        recipient_id: recipientId,
        body,
      })
      .select(MESSAGE_SELECT_COLUMNS)
      .single();

    if (error) {
      logMutation("sendMessage", { stage: "error", error: error?.message || error });
      throw error;
    }
    logMutation("sendMessage", { stage: "success", messageId: data.id });
    return data;
  } catch (err) {
    logMutation("sendMessage", { stage: "exception", error: err?.message || String(err) });
    throw err;
  }
}

export async function getOrCreateConversation({
  supabase,
  businessId,
  session,
}: {
  supabase?: any;
  businessId: string;
  session?: any;
}) {
  const client = supabase ?? getSupabaseBrowserClient();
  if (!client) return null;

  const activeSession =
    session ?? (await requireSession(client, { label: "getOrCreateConversation" }));
  const resolvedCustomerId = activeSession?.user?.id ?? null;

  logMutation("getOrCreateConversation", {
    stage: "start",
    customerId: resolvedCustomerId,
    businessId,
    hasAccessToken: Boolean(activeSession?.access_token),
  });

  const { data, error } = await client.rpc("get_or_create_conversation", {
    customer_id: resolvedCustomerId,
    business_id: businessId,
  });

  if (!error) {
    logMutation("getOrCreateConversation", { stage: "success", conversationId: data });
    return data || null;
  }

  const message = (error?.message || "").toLowerCase();
  const missingRpc =
    error?.code === "PGRST202" ||
    message.includes("could not find the function") ||
    message.includes("function public.get_or_create_conversation");

  if (!missingRpc) {
    logMutation("getOrCreateConversation", {
      stage: "error",
      error: error?.message || error,
    });
    throw error;
  }

  const { data: existing, error: existingError } = await client
    .from("conversations")
    .select("id")
    .eq("customer_id", resolvedCustomerId)
    .eq("business_id", businessId)
    .maybeSingle();

  if (existingError) {
    logMutation("getOrCreateConversation", {
      stage: "fallback_error",
      error: existingError?.message || existingError,
    });
    throw existingError;
  }
  if (existing?.id) {
    logMutation("getOrCreateConversation", {
      stage: "fallback_existing",
      conversationId: existing.id,
    });
    return existing.id;
  }

  const { data: upserted, error: upsertError } = await client
    .from("conversations")
    .upsert(
      { customer_id: resolvedCustomerId, business_id: businessId },
      { onConflict: "customer_id,business_id" }
    )
    .select("id")
    .single();

  if (upsertError) {
    logMutation("getOrCreateConversation", {
      stage: "fallback_error",
      error: upsertError?.message || upsertError,
    });
    throw upsertError;
  }
  logMutation("getOrCreateConversation", {
    stage: "fallback_success",
    conversationId: upserted?.id || null,
  });
  return upserted?.id || null;
}

export async function markConversationRead({
  supabase,
  conversationId,
}: {
  supabase?: any;
  conversationId: string;
}) {
  const client = supabase ?? getSupabaseBrowserClient();
  if (!client) return null;

  const activeSession = await requireSession(client, {
    label: "markConversationRead",
  });
  logMutation("markConversationRead", {
    stage: "start",
    conversationId,
    sessionUserId: activeSession?.user?.id ?? null,
  });

  const { error } = await client.rpc("mark_conversation_read", {
    conversation_id: conversationId,
  });

  if (error) {
    logMutation("markConversationRead", { stage: "error", error: error?.message || error });
    throw error;
  }
  logMutation("markConversationRead", { stage: "success" });
  return true;
}

export async function fetchUnreadTotal({
  supabase,
  userId,
  role,
}: {
  supabase?: any;
  userId: string;
  role: "customer" | "business";
}) {
  const client = supabase ?? getSupabaseBrowserClient();
  if (!client) return 0;
  if (!userId) return 0;

  const { data, error } = await client.rpc("unread_total", {
    p_role: role,
    p_uid: userId,
  });

  if (!error) return Number(data || 0);

  const message = (error?.message || "").toLowerCase();
  const missingRpc =
    error?.code === "PGRST202" ||
    message.includes("could not find the function") ||
    message.includes("function public.unread_total");

  if (!missingRpc) throw error;

  const field = role === "business" ? "business_id" : "customer_id";
  const { data: rowsData, error: rowError } = await client
    .from("conversations")
    .select("customer_unread_count, business_unread_count")
    .eq(field, userId);

  if (rowError) throw rowError;
  const rows = Array.isArray(rowsData) ? rowsData : [];
  return rows.reduce((sum, row) => sum + getUnreadCount(row, role), 0);
}

async function fetchProfilesByIds({
  supabase,
  ids,
}: {
  supabase?: any;
  ids: Array<string | null | undefined>;
}) {
  const client = supabase ?? getSupabaseBrowserClient();
  if (!client) return {};
  const uniqueIds = Array.from(
    new Set(ids.filter((id): id is string => Boolean(id)))
  );
  if (uniqueIds.length === 0) return {};

  return fetchPublicProfilesByIds(client, uniqueIds);
}
